/* ------------------------------------------------------------------
   chartSubsections.ts — server-safe metadata for the Charts section.

   FinancialsTab.tsx is a client component, so any non-component exports
   from it become client-reference proxies when imported from a server
   component (the `.map is not a function` trap). Keep the pure data and
   types here so both sides can import them cleanly.
   ------------------------------------------------------------------ */

import { CHART_COLORS } from '@/lib/chart-palette';

export type AlternateMode =
  | 'debtcash' | 'profits' | 'margins' | 'area' | 'returns'
  | 'cashflows' | 'cashflowmargins' | 'efficiency' | 'revenuegrowth'
  | 'growthcomparison' | 'valuation' | 'cumulativereturns'
  | 'shortTermReturn' | 'mediumTermReturn' | 'longTermReturn'
  | 'currentMetrics' | 'priceRisk' | 'roeRoaRoce' | 'fcfNiMargin'
  | 'debtLevels' | 'debtRatios' | 'dividendsBuyback' | 'capex';

export interface MetricConfig {
  key: string;
  metricKey?: string;
  title: string;
  forecastKey?: string;
  color: string;
  alternateMode?: AlternateMode;
  divisor: number;
  includeLtm?: boolean;
  sectionHeader?: string;
  /** Cumulative Return chart: plot Total Return only (no Price Return). */
  totalReturnOnly?: boolean;
}

/**
 * Single chart registry — one definition per metric (title, color family,
 * render mode, data keys). Every chart set, X-Ray, the Financials table and the
 * Verdict boards reference these, so a chart looks and reads the same wherever
 * it appears (no "Margins" here and "Profit Margins" there).
 */
export const CHARTS = {
  '52wreturn': { key: '52wreturn', title: '52 Week Return', color: CHART_COLORS.market, divisor: 1 },
  capex: { key: 'capex', title: 'Capex', color: CHART_COLORS.capex, alternateMode: 'capex', divisor: 1000 },
  cashflowmargins: { key: 'cashflowmargins', title: 'Cash Flow Margins', color: CHART_COLORS.operatingCash, alternateMode: 'cashflowmargins', divisor: 1 },
  cashflows: { key: 'cashflows', title: 'Cash Flows', color: CHART_COLORS.operatingCash, alternateMode: 'cashflows', divisor: 1000 },
  cumulativereturns: { key: 'cumulativereturns', title: 'Cumulative Return', color: CHART_COLORS.market, alternateMode: 'cumulativereturns', divisor: 1 },
  currentMetrics: { key: 'currentMetrics', title: 'Current Metrics', color: '#a78bfa', alternateMode: 'currentMetrics', divisor: 1 },
  debtLevels: { key: 'debtLevels', title: 'Debt', color: CHART_COLORS.debt, alternateMode: 'debtLevels', divisor: 1000 },
  debtRatios: { key: 'debtRatios', title: 'Debt / EBITDA', color: CHART_COLORS.debt, alternateMode: 'debtRatios', divisor: 1 },
  dividendsBuyback: { key: 'dividendsBuyback', title: 'Payouts vs FCF', color: CHART_COLORS.dividends, alternateMode: 'dividendsBuyback', divisor: 1000 },
  divyield: { key: 'divyield', title: 'Dividend Yield', forecastKey: 'divyield_forecast', color: CHART_COLORS.dividends, alternateMode: 'area', divisor: 1, includeLtm: false },
  dps: { key: 'dps', title: 'Dividend per Share', forecastKey: 'dps_forecast', color: CHART_COLORS.dividends, divisor: 1 },
  efficiency: { key: 'efficiency', title: 'Efficiency & Debt', color: '#a78bfa', alternateMode: 'efficiency', divisor: 1 },
  eps: { key: 'eps', title: 'EPS', forecastKey: 'eps_forecast', color: CHART_COLORS.earnings, divisor: 1 },
  evebitda: { key: 'evebitda', title: 'EV/EBITDA', forecastKey: 'evebitda_forecast', color: CHART_COLORS.ebitda, alternateMode: 'area', divisor: 1, includeLtm: false },
  evsales: { key: 'evsales', title: 'EV/Sales', forecastKey: 'evsales_forecast', color: CHART_COLORS.revenue, alternateMode: 'area', divisor: 1, includeLtm: false },
  fcfNiMargin: { key: 'fcfNiMargin', title: 'FCF Margin vs NI Margin', color: CHART_COLORS.fcf, alternateMode: 'fcfNiMargin', divisor: 1 },
  fcfyield: { key: 'fcfyield', title: 'FCF Yield', forecastKey: 'fcfyield_forecast', color: CHART_COLORS.fcf, alternateMode: 'area', divisor: 1, includeLtm: false },
  fcfyield_cf: { key: 'fcfyield_cf', metricKey: 'fcfyield', title: 'FCF Yield', forecastKey: 'fcfyield_forecast', color: CHART_COLORS.fcf, alternateMode: 'area', divisor: 1, includeLtm: false },
  growthcomparison: { key: 'growthcomparison', title: 'Revenue Growth Summary', color: '#4ade80', alternateMode: 'growthcomparison', divisor: 1 },
  longTermReturn: { key: 'longTermReturn', title: 'Long-Term Return', color: '#10b981', alternateMode: 'longTermReturn', divisor: 1 },
  margins: { key: 'margins', title: 'Margins', color: CHART_COLORS.ebitda, alternateMode: 'margins', divisor: 1 },
  mediumTermReturn: { key: 'mediumTermReturn', title: 'Medium-Term Return', color: '#a78bfa', alternateMode: 'mediumTermReturn', divisor: 1 },
  pe: { key: 'pe', title: 'P/E', forecastKey: 'pe_forecast', color: CHART_COLORS.earnings, alternateMode: 'area', divisor: 1, includeLtm: false },
  priceclose: { key: 'priceclose', title: 'Price', color: CHART_COLORS.market, alternateMode: 'area', divisor: 1, includeLtm: true },
  priceRisk: { key: 'priceRisk', title: 'Price Risk', color: '#f59e0b', alternateMode: 'priceRisk', divisor: 1 },
  profits: { key: 'profits', title: 'Profits', color: CHART_COLORS.earnings, alternateMode: 'profits', divisor: 1000 },
  returns: { key: 'returns', title: 'Stock Total Return', color: '#a78bfa', alternateMode: 'returns', divisor: 1 },
  revenue: { key: 'revenue', title: 'Revenue', forecastKey: 'revenue_forecast', color: CHART_COLORS.revenue, alternateMode: 'area', divisor: 1000 },
  revenuegrowth: { key: 'revenuegrowth', title: 'Revenue Growth', color: '#4ade80', alternateMode: 'revenuegrowth', divisor: 1 },
  roeRoaRoce: { key: 'roeRoaRoce', title: 'Efficiency', color: '#10b981', alternateMode: 'roeRoaRoce', divisor: 1 },
  shares: { key: 'shares', title: 'Shares Outstanding', color: CHART_COLORS.market, alternateMode: 'area', divisor: 1 },
  shortTermReturn: { key: 'shortTermReturn', title: 'Short-Term Return', color: '#38bdf8', alternateMode: 'shortTermReturn', divisor: 1 },
  stockreturn: { key: 'stockreturn', metricKey: 'cumulativereturns', title: 'Stock Return', color: CHART_COLORS.market, alternateMode: 'cumulativereturns', divisor: 1, totalReturnOnly: true },
  valuation: { key: 'valuation', title: 'EV & Market Cap', color: CHART_COLORS.enterprise, alternateMode: 'valuation', divisor: 1000 },
} satisfies Record<string, MetricConfig>;

/* ---------- individual chart sets ---------- */

export const GROWTH_CHART_SET: MetricConfig[] = [
  CHARTS.revenue,
  CHARTS.revenuegrowth,
  CHARTS.profits,
  CHARTS.cashflows,
  CHARTS.eps,
  CHARTS.growthcomparison,
];

/* Anchored Growth dashboard order:
   Revenue (hero) → bottom carousel: Revenue Growth, Profits, Cash Flows, then the rest. */
export const GROWTH_ANCHORED_SET: MetricConfig[] = [
  CHARTS.revenue,
  CHARTS.revenuegrowth,
  CHARTS.profits,
  CHARTS.cashflows,
  CHARTS.eps,
  CHARTS.growthcomparison,
];

/** First three charts: hero + default bottom pair (used by layout helpers). */
export const GROWTH_DASHBOARD_SET: MetricConfig[] = GROWTH_ANCHORED_SET.slice(0, 3);

export const QUALITY_CHART_SET: MetricConfig[] = [
  CHARTS.margins,
  CHARTS.cashflowmargins,
  CHARTS.fcfNiMargin,
  CHARTS.efficiency,
  CHARTS.debtLevels,
  CHARTS.debtRatios,
];

/* Hero: Profit Margins → Cash Flow Margins + FCF vs NI below; rest in carousel. */
export const QUALITY_DASHBOARD_SET: MetricConfig[] = [
  CHARTS.margins,
  CHARTS.cashflowmargins,
  CHARTS.fcfNiMargin,
];

const QUALITY_CAROUSEL_EXTRA: MetricConfig[] = QUALITY_CHART_SET.filter(
  (c) => !QUALITY_DASHBOARD_SET.some((d) => d.key === c.key),
);

export const QUALITY_ANCHORED_SET: MetricConfig[] = [
  ...QUALITY_DASHBOARD_SET,
  ...QUALITY_CAROUSEL_EXTRA,
];

export const VALUE_CHART_SET: MetricConfig[] = [
  CHARTS.evebitda,
  CHARTS.fcfyield,
  CHARTS.evsales,
  CHARTS.pe,
  CHARTS.divyield,
  CHARTS.currentMetrics,
];

/* Hero: P/E → EV/EBITDA + FCF Yield below; rest in carousel. */
export const VALUE_DASHBOARD_SET: MetricConfig[] = [
  CHARTS.pe,
  CHARTS.evebitda,
  CHARTS.fcfyield,
];

const VALUE_CAROUSEL_EXTRA: MetricConfig[] = VALUE_CHART_SET.filter(
  (c) => !VALUE_DASHBOARD_SET.some((d) => d.key === c.key),
);

export const VALUE_ANCHORED_SET: MetricConfig[] = [
  ...VALUE_DASHBOARD_SET,
  ...VALUE_CAROUSEL_EXTRA,
];

export const MOMENTUM_CHART_SET: MetricConfig[] = [
  CHARTS.priceclose,
  CHARTS.valuation,
  CHARTS.cumulativereturns,
  CHARTS.shortTermReturn,
  CHARTS.mediumTermReturn,
  CHARTS.longTermReturn,
];

/* Hero: Cumulative Return → Price + EV/Market Cap below. No carousel extras. */
export const MOMENTUM_DASHBOARD_SET: MetricConfig[] = [
  CHARTS.cumulativereturns,
  CHARTS.priceclose,
  CHARTS.valuation,
];

export const ANTIFRAGILITY_CHART_SET: MetricConfig[] = [
  CHARTS.priceRisk,
  CHARTS.eps,
  CHARTS.roeRoaRoce,
  CHARTS.debtLevels,
  CHARTS.fcfNiMargin,
  CHARTS.debtRatios,
];

// GEAR+ default 6-chart set (5 ключевых)
export const DEFAULT_CHART_SET: MetricConfig[] = [
  CHARTS.revenue,
  CHARTS.eps,
  CHARTS.profits,
  CHARTS.pe,
  CHARTS.margins,
  CHARTS.returns,
];

// Quality Compounder = QualityGrowth
export const QUALITY_GROWTH_CHART_SET: MetricConfig[] = [
  CHARTS.revenue,
  CHARTS.eps,
  CHARTS.profits,
  CHARTS.margins,
  CHARTS.cashflows,
  CHARTS.efficiency,
];

// Growth Rocket = GrowthMomentum
export const GROWTH_MOMENTUM_CHART_SET: MetricConfig[] = [
  CHARTS.valuation,
  CHARTS['52wreturn'],
  CHARTS.cumulativereturns,
  CHARTS.revenue,
  CHARTS.profits,
  CHARTS.cashflows,
];

// Smart Value = ValueMomentum
export const VALUE_MOMENTUM_CHART_SET: MetricConfig[] = [
  CHARTS.valuation,
  CHARTS['52wreturn'],
  CHARTS.cumulativereturns,
  CHARTS.pe,
  CHARTS.fcfyield,
  CHARTS.evebitda,
];

// Cash Flow focused subsection — OCF / FCF / CapEx etc.
export const CASHFLOWS_CHART_SET: MetricConfig[] = [
  CHARTS.cashflows,
  CHARTS.cashflowmargins,
  CHARTS.fcfNiMargin,
  CHARTS.fcfyield,
];

// Dividends & Buyback subsection.
export const DIVIDENDS_CHART_SET: MetricConfig[] = [
  CHARTS.dividendsBuyback,
  CHARTS.dps,
  CHARTS.divyield,
  CHARTS.shares,
];

/* ---------- lookup by strategy code (used in tab-mode) ---------- */

export const CHART_SETS: Record<string, MetricConfig[]> = {
  GEAR5FScore: DEFAULT_CHART_SET,
  PureGrowth: GROWTH_CHART_SET,
  PureQuality: QUALITY_CHART_SET,
  PureValue: VALUE_CHART_SET,
  PureMomentum: MOMENTUM_CHART_SET,
  PureAntifragility: ANTIFRAGILITY_CHART_SET,
  ValueMomentum: VALUE_MOMENTUM_CHART_SET,
  GrowthMomentum: GROWTH_MOMENTUM_CHART_SET,
  QualityGrowth: QUALITY_GROWTH_CHART_SET,
};

/* ===== Ordered subsections used by the anchored layout =====
   Source of truth for the Charts secondary nav in BastionCompass.
   Each entry produces one anchored heading on the page
   (`<h3 id={`charts-${id}`}>`) followed by its chart grid. Order
   here = order in the sidebar and in the rendered document. */

export interface ChartSubsection {
  id: string;        // anchor suffix, used as `charts-${id}`
  label: string;     // sidebar + heading label
  configs: MetricConfig[];
}

/* ---------- per-tab 3-chart setups ----------
   Each Charts tab is a fixed 3-chart "setup" (hero + two below). No
   carousels: the bottom row always holds exactly two charts. */

/* Growth: Revenue (hero) → Revenue Growth + Profits. */
export const GROWTH_TAB_SET: MetricConfig[] = [
  CHARTS.revenue,
  CHARTS.revenuegrowth,
  CHARTS.profits,
];

/* Cash Flows: Cash Flows (hero) → Capex + FCF Yield. Sidebar = Quality. */
export const CASHFLOWS_TAB_SET: MetricConfig[] = [
  CHARTS.cashflows,
  CHARTS.capex,
  CHARTS.fcfyield,
];

/* Quality: Profit Margins only (CF margins + Efficiency & Debt are too heavy;
   those answers live in Strategy Metrics + the Quality checklist). */
export const QUALITY_TAB_SET: MetricConfig[] = [
  CHARTS.margins,
];

/* Value: P/E (hero) → EV/EBITDA + EV/Sales. */
export const VALUE_TAB_SET: MetricConfig[] = [
  CHARTS.pe,
  CHARTS.evebitda,
  CHARTS.evsales,
];

/* Dividends: Dividend Yield (hero) → DPS bars + Dividends & Buyback.
   When the company pays no dividend, FinancialsTab swaps this for a
   buyback+FCF chart (or an empty notice) via getDividendsTabConfigs. */
export const DIVIDENDS_TAB_SET: MetricConfig[] = [
  CHARTS.divyield,
  CHARTS.dps,
  CHARTS.dividendsBuyback,
];

export const DIVIDENDS_BUYBACK_ONLY_SET: MetricConfig[] = [
  { ...CHARTS.dividendsBuyback, title: 'Buybacks & Free Cash Flow' },
];

/** Chart configs for the Dividends chapter, adapted to cash-return profile. */
export function getDividendsTabConfigs(
  mode: 'full' | 'buyback-only' | 'none',
): MetricConfig[] {
  if (mode === 'full') return DIVIDENDS_TAB_SET;
  if (mode === 'buyback-only') return DIVIDENDS_BUYBACK_ONLY_SET;
  return [];
}

export const CHART_SUBSECTIONS: ChartSubsection[] = [
  // Growth and Price Momentum are the free "hook" chapters (Growth first: the
  // terminal-board chapter leads after the scores). Value comes next as the
  // strongest selling chapter (first section behind the paywall). Quality
  // follows, then Dividends (see stock-offer-roadmap). No Cash Flows chapter
  // (Oct 2026): its charts live in Growth (Cash Flows, Capex), Quality (cash
  // flow margins, FCF vs NI) and Value (FCF yield); its review in Quality.
  { id: 'growth',         label: 'Growth',         configs: GROWTH_TAB_SET },
  { id: 'momentum',       label: 'Price Momentum', configs: MOMENTUM_DASHBOARD_SET },
  { id: 'value',          label: 'Value',          configs: VALUE_TAB_SET },
  { id: 'quality',        label: 'Quality',        configs: QUALITY_TAB_SET },
  // Dividends AND buybacks: for many payers (NVDA: 1.1% buybacks vs 0.4%
  // dividend) the cash comes back mainly through repurchases.
  { id: 'dividends',      label: 'Dividends & Buybacks', configs: DIVIDENDS_TAB_SET },
  { id: 'gear',           label: 'Verdict',            configs: DEFAULT_CHART_SET },
];

/**
 * Test / Part 1 packing: render Growth → Cash Flows → … as a vertical
 * stack (one under another) instead of a single Charts panel with tabs.
 * Flip to false to restore the tab bar.
 */
export const STACK_FACTOR_SECTIONS = true;

/** DOM id for a stacked factor dashboard (Compass + LazyAnchoredSection). */
export function chartSectionAnchorId(subsectionId: string): string {
  return `charts-${subsectionId}`;
}

/** Chart keys that rely on enterprise value — not meaningful for Financials
    (banks / insurers have no comparable EV, so EV/EBITDA, EV/Sales, EV/EBIT
    charts read as noise). */
const EV_BASED_CHART_KEYS = new Set(['evebitda', 'evsales', 'evebit']);

export function isFinancialsStock(stock: { Sector?: unknown; sector?: unknown }): boolean {
  const s = String(stock.Sector ?? stock.sector ?? '').toLowerCase();
  return s === 'financials' || s.includes('financial');
}

/** Shown in place of Cash Flows charts + assessment for banks / insurers. */
export const FINANCIALS_NO_FCF_COPY =
  'Free cash flow is not a meaningful metric for banks and insurers. Capex, FCF yield, and the Cash Flow Assessment do not apply here. Use earnings, book value, and dividends instead.';

/** ETFs only have momentum metrics; skip empty Growth/Quality/Value/Strategy charts. */
export function getChartSubsectionsForStock(
  stock: { type?: string | null; Sector?: unknown; sector?: unknown },
): ChartSubsection[] {
  const isETF = String(stock.type ?? '').toLowerCase().trim() === 'etf';
  if (isETF) {
    return CHART_SUBSECTIONS.filter((s) => s.id === 'momentum');
  }
  if (isFinancialsStock(stock)) {
    // Financials: drop EV multiples, and empty the Cash Flows chapter (FCF /
    // Capex / FCF yield are not meaningful for banks and insurers). A notice
    // is rendered in place of the charts + assessment.
    return CHART_SUBSECTIONS.map((s) => {
      if (s.id === 'cash-flows') return { ...s, configs: [] };
      const filtered = s.configs.filter((c) => !EV_BASED_CHART_KEYS.has(c.key));
      return filtered.length === s.configs.length ? s : { ...s, configs: filtered };
    });
  }
  return CHART_SUBSECTIONS;
}
